import { fromJS, List as IList, Map as IMap } from 'immutable';
import { createSelector } from 'reselect';

import {
    ACTIVE_PROMO,
    CUSTOM_REWARD_CARD_RECIPIENT_STORE_KEY,
    DIGITAL_PROMO_REWARD_CARDS,
    getPromoRecipientHash,
    PROMO_LIMIT_EXCEEDED,
    PROMO_RECIPIENT_FIELDS,
    PROMO_STATUS,
    REJECTED_PROMO,
    SHOW_BANNER,
    PROMO_NUDGES, PLASTIC_PROMO_REWARD_CARDS,
    SHIPPING_PROMO_REWARDS, DEFAULT_PROMO_CODE
} from './promoModule';
import {
    getCurrencyCode, getGrandTotal,
    getItemDenomination,
    getPaidItems,
    isPromoItem
} from '../productCartHandler/productCartSelectors';
import { getPromoState, getPromoRewards, getIsRewardsRequestActive } from './sharedCartPromoSelectors';
import { getIsOldUiPreferred } from '../app/bootstrap';

export { getIsRewardsRequestActive };


export const getActivePromo = createSelector(
    getPromoState,
    promoState => promoState.get(ACTIVE_PROMO, null)
);

export const getPromoTerms = createSelector(
    getActivePromo,
    promo => (promo ? promo.get('terms') : '')
);

export const getPromoBannerAltText = createSelector(
    getActivePromo,
    (promo) => {
        const bannerHtml = promo ? promo.get('digitalBannerSmall') : '';
        if (!bannerHtml || typeof bannerHtml !== 'string') return '';
        const match = bannerHtml.match(/alt="([^"]*)"/);
        return match ? match[1] : '';
    }
);

export const getActivePromoCode = createSelector(
    getActivePromo,
    promo => (promo ? promo.get('promoCode') : null)
);

export const getDefaultPromoCode = createSelector(
    getPromoState,
    promoState => promoState.get(DEFAULT_PROMO_CODE, '')
);

export const getCartQualifiesForPromo = createSelector(
    getPromoRewards,
    cartqualify => (cartqualify ? cartqualify.get('cartQualifies') : false)
);

export const getBonusCards = createSelector(
    getPromoRewards,
    bonusCards => (bonusCards ? bonusCards.get('electronicRewardCards', IList()) : IList())
);

export const getFirstBonusCard = createSelector(
    getBonusCards,
    bonusCard => (bonusCard ? bonusCard.get(0, IMap()) : IMap())
);

export const getPromoStatus = createSelector(
    getPromoState,
    promoState => promoState.get(PROMO_STATUS, null)
);

export const getActivePromoCodeWithRewards = createSelector(
    [getActivePromo, getBonusCards],
    (promo, bonusCard) => (promo && bonusCard !== IList() ? promo.get('promoCode') : null)
);

export const getEffectiveActivePromoCode = createSelector(
    [getActivePromoCodeWithRewards, getActivePromoCode],
    (ActivePromoCodeWithRewards, ActivePromoCode) => (getIsOldUiPreferred() ? ActivePromoCodeWithRewards : ActivePromoCode)
);

export const getRejectedPromo = createSelector(
    getPromoState,
    promoState => promoState.get(REJECTED_PROMO, null)
);

export const getPromoLimitExceeded = createSelector(
    getPromoState,
    promoState => promoState.get(PROMO_LIMIT_EXCEEDED, false)
);

export const getBannerDisplayStatus = createSelector(
    getPromoState,
    promoState => promoState.get(SHOW_BANNER, false)
);

export const hasDigitalBanners = createSelector(
    getActivePromo,
    promo => (promo ? !!promo.get('digitalBannerSmall') && !!promo.get('digitalBannerLarge') : false)
);

export const hasSoldOutBanners = createSelector(
    getActivePromo,
    promo => (promo ? !!promo.get('soldOutBannerSmall') && !!promo.get('soldOutBannerLarge') : false)
);

export const getDigitalBanners = createSelector(
    getActivePromo,
    promo => (promo ? {
        small: promo.get('digitalBannerSmall'),
        large: promo.get('digitalBannerLarge')
    } : null)
);

export const getSoldOutBanners = createSelector(
    getActivePromo,
    promo => (promo ? {
        small: promo.get('soldOutBannerSmall'),
        large: promo.get('soldOutBannerLarge')
    } : null)
);

export const activePromoSoldOut = createSelector(
    getActivePromo,
    promo => (promo ? promo.get('soldOut') : false)
);

export const hasPlasticBanners = createSelector(
    getActivePromo,
    promo => (promo ? !!promo.get('plasticBannerSmall') && !!promo.get('plasticBannerLarge') : false)
);

export const getPlasticBanners = createSelector(
    getActivePromo,
    promo => (promo ? {
        small: promo.get('plasticBannerSmall'),
        large: promo.get('plasticBannerLarge')
    } : null)
);




export const newItemIsPlastic = (state, isPlasticBanner) => isPlasticBanner;

export const getPromoBanners = createSelector(
    [
        newItemIsPlastic,
        hasDigitalBanners,
        hasPlasticBanners,
        getDigitalBanners,
        getPlasticBanners,
        activePromoSoldOut,
        getSoldOutBanners
    ],
    (
        isPlastic,
        hasDigital,
        hasPlastic,
        digital,
        plastic,
        soldOut,
        soldOutBanners
    ) => {
        if (soldOut) {
            return soldOutBanners;
        } if (hasPlastic && (isPlastic || !hasDigital)) {
            return plastic;
        } if (hasDigital) {
            return digital;
        }
        return null;
    }
);

export const getDigitalPromoCards = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(DIGITAL_PROMO_REWARD_CARDS, IList()) : IList())
);

export const getPlasticPromoCards = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(PLASTIC_PROMO_REWARD_CARDS, IList()) : IList())
);

export const getPromoRewardCards = createSelector(
    getDigitalPromoCards,
    getPlasticPromoCards,
    (digital, plastic) => digital.concat(plastic)
);

export const getPromoRewardsCount = createSelector(
    getPromoRewardCards,
    rewards => rewards.size
);

export const hasPromoRewards = createSelector(
    getPromoRewardsCount,
    count => count > 0
);


export const getCustomRewardCardRecipients = createSelector(
    getPromoState,
    promo => promo.get(CUSTOM_REWARD_CARD_RECIPIENT_STORE_KEY, IMap())
);

export const getCurrencyCodes = createSelector(
    getPaidItems,
    items => items.map(item => getCurrencyCode(item)).toSet()
);

export const isPromoSelfBonus = promo => promo.get('selfBonus', false);

export const getPromoCertType = promo => promo.get('certType');

export const getItemId = (item) => {
    if (isPromoItem(item)) {
        const recipientHash = getPromoRecipientHash(item);
        return `${parseFloat(item.get('cardValue'))}-${item.getIn(['faceplate', 'code'])}-${recipientHash}`;
    }
    return item.get('id');
};

export const getIsNonDenomItem = item => (
    getItemDenomination(item) === 0.01
);

export const getPromoNudgesValue = createSelector(
    getPromoState,
    values => (values ? values.get(PROMO_NUDGES, '') : '')
);

/**
 * Get all of the Promo items from the redux store.  This rolls up all of the same promo reward card
 * maps into one promo "item" map that has the shape of an item.
 * @param {Immutable.Map} state The current state.
 * @returns {Immutable.List.<Immutable.Map>} List of promo items suitable for display per recipient.
 *
 */
export const getPromoItems = createSelector(
    getPromoRewardCards,
    getCurrencyCodes,
    (rewards, currencyCodes) => {
        if (rewards.size <= 0) return IList();

        // the first currency should be the currency for the entire order
        const currencyCode = currencyCodes.first();

        const promoItems = IMap().withMutations((itemsMap) => {
            // using withMutations makes the itemsMap object in this block mutable
            // so that we don't have to create so many intermediate maps during the iteration on rewards
            rewards.forEach((reward) => {
                const deliveryMethod = reward.get('deliveryMethod');

                
                
                let rewardKey = getItemId(reward);

                
                if (deliveryMethod === 'PLCMT') {
                    rewardKey = `PLCMT-${rewardKey}`;
                }

                if (!itemsMap.has(rewardKey)) {
                    
                    const missingProperties = {
                        id: rewardKey,
                        quantity: 1,
                        isEditable: false
                    };
                    
                    itemsMap.set(rewardKey, reward.merge(missingProperties));
                } else {
                    
                    itemsMap.updateIn([rewardKey, 'quantity'], quantity => quantity + 1);
                }

                
                
                if (!itemsMap.getIn([rewardKey, 'currencyCode'])) {
                    itemsMap.setIn([rewardKey, 'currencyCode'], currencyCode);
                }
            });
        });

        
        return promoItems.toList();
    }
);

export const getPromoLineItems = createSelector(
    getPromoItems,
    (rewards) => {
        const promoItems = IMap().withMutations((itemsMap) => {
            rewards.forEach((reward) => {
                
                const rewardKey = `${parseFloat(reward.get('cardValue'))}-${reward.getIn(['faceplate', 'code'])}`;

                if (!itemsMap.has(rewardKey)) {
                    itemsMap.set(rewardKey, reward);
                } else {
                    itemsMap.updateIn([rewardKey, 'quantity'], quantity => quantity + reward.get('quantity'));
                }
            });
            return itemsMap;
        });

        return promoItems.toList();
    }
);

const getFaceplateImageUrl = (card) => {
    if (!IMap.isMap(card)) return '';
    return card.getIn(['faceplate', 'imageURL'], '');
};

/**
 * Get promo recipient to use for displaying the promo address.
 * @param {Immutable.Map} promo The promo.
 * @return {Immutable.List} A list of maps with Promo rewards and Faceplate Image URL
 */
export const getTransformedPromoItems = createSelector(
    getPromoItems,
    digitalRewards => digitalRewards.map(card => card.set('imageUrl', getFaceplateImageUrl(card)))
);

export const getPromoCertRecipient = (promo, itemRecipients, selfBonus, activeCardDetails) => {
    const recipient = promo.get('recipient');
    const senderName = activeCardDetails.selfBuy ? activeCardDetails.yourName : activeCardDetails.senderName;
    const senderEmail = activeCardDetails.selfBuy ? activeCardDetails.yourEmail : activeCardDetails.senderEmail;
    const recipientNameFromCard = recipient.get('full_name') || recipient.get('recipient_name');
    const recipientEmailFromCard = recipient.get('email');
    const recipientName = selfBonus
        ? (senderName || recipientNameFromCard)
        : recipientNameFromCard;
    const recipientEmail = selfBonus
        ? (senderEmail || recipientEmailFromCard)
        : recipientEmailFromCard;

    return fromJS({
        [PROMO_RECIPIENT_FIELDS.RECIPIENT_NAME]: recipientName,
        [PROMO_RECIPIENT_FIELDS.FIRST_NAME]: recipient.get('first_name'),
        [PROMO_RECIPIENT_FIELDS.LAST_NAME]: recipient.get('last_name'),
        [PROMO_RECIPIENT_FIELDS.EMAIL]: recipientEmail,
        [PROMO_RECIPIENT_FIELDS.PHONE]: recipient.get('phone'),
        [PROMO_RECIPIENT_FIELDS.STREET1]: recipient.get('street'),
        [PROMO_RECIPIENT_FIELDS.STREET2]: recipient.get('street2'),
        [PROMO_RECIPIENT_FIELDS.CITY]: recipient.get('city'),
        [PROMO_RECIPIENT_FIELDS.STATE]: recipient.get('state'),
        [PROMO_RECIPIENT_FIELDS.ZIP_CODE]: recipient.get('zip_code'),
        [PROMO_RECIPIENT_FIELDS.COUNTRY_CODE]: recipient.get('country_code')
    });
};
export const getCustomPromoCertRecipient = (promo) => {
    const recipient = fromJS(promo);
    return fromJS({
        [PROMO_RECIPIENT_FIELDS.RECIPIENT_NAME]: recipient.get('full_name') || recipient.get('recipient_name'),
        [PROMO_RECIPIENT_FIELDS.FIRST_NAME]: recipient.get('first_name'),
        [PROMO_RECIPIENT_FIELDS.LAST_NAME]: recipient.get('last_name'),
        [PROMO_RECIPIENT_FIELDS.EMAIL]: recipient.get('email')
    });
};


export const getParentCartTokenForItem = (state, item) => {
    const customCardRecipients = getCustomRewardCardRecipients(state).toJS();
    const parentCardCartToken = Object.keys(customCardRecipients).find((recipientKey) => {
        const recipient = customCardRecipients[recipientKey];
        if ('selectedRecipient' in recipient) {
            if (item.get('id') === recipient.selectedRecipient.split('_')[0]) {
                return recipient.selectedRecipient;
            }
        }
        return undefined;
    });
    if (!parentCardCartToken) {
        return { parentCardCartToken: undefined, isSender: false };
    }

    const { selectedRecipient } = customCardRecipients[parentCardCartToken];
    const isSender = selectedRecipient.split('_')[1] === 'sender';

    return { parentCardCartToken, isSender };
};

export const getPromoDetailsForSegment = createSelector(
    [
        getActivePromo,
        getPromoBannerAltText,
        getGrandTotal,
        getCurrencyCode
    ],
    (promo, promoDetail, cartValue, Currency) => ({
        promo_code: promo && promo.get('promoCode'),
        promo_detail: promoDetail,
        cart_value: cartValue,
        Currency,
        url: new URL(window.location.href)
    })
);

export const getPromoLabel = promo => ((promo && IMap.isMap(promo)) ? promo.get('displayLabel') : '');


export const getShippingPromoData = createSelector(
    getPromoRewards,
    rewards => (rewards ? rewards.get(SHIPPING_PROMO_REWARDS) : IList())
);

export const getShippingCostData = (state) => {
    const promoState = state.get('promo');
    return promoState ? promoState.get('shippingCostData', IMap()) : IMap();
};
